<style>
  /* 
    The [open] is required for the .close() method to work, otherwise the
    display: flex applies to the closed dialog, which is supposed to have
    display: none. 
  */
  dialog[open] {
    border: 1px solid var(--fg-color);
    box-shadow: 4px 4px 0 0 var(--fg-color);
    padding: 0.25em;
    z-index: 10;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: stretch;
    gap: 0.25em;
    width: var(--width);
    min-width: var(--width);
    max-width: var(--width);
    height: var(--height);
    min-height: var(--height);
    max-height: var(--height);
  }

  .small-shadow {
    box-shadow: 2px 2px 0 0 var(--fg-color) !important;
  }

  .top {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: stretch;
    gap: 0.25em;
  }

  .top .drag {
    flex-grow: 1;
    cursor: grab;
    height: 1.5em;
    touch-action: none;
    border: 1px solid var(--fg-color);
  }

  .top .drag:active {
    cursor: grabbing;
  }

  .main {
    flex-grow: 1;
    overflow: auto;
  }

  .resize {
    position: absolute;
    width: 16px;
    height: 16px;
    bottom: -8px;
    right: -8px;
    border: 0;
    background: none;
    cursor: nwse-resize;
    touch-action: none;
    z-index: 10;
  }

  /* TODO: Add better indicator for the drag handle. */
  .resize::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 7px;
    height: 7px;
    border: 1px solid var(--fg-color);
    background: var(--fg-color);
  }

  .resize:hover::after {
    outline: 1px solid var(--fg-color);
  }
</style>

<script>
  import Button from "./Button.svelte";
  import { randomId, nextZIndex } from "../lib/helpers.js";

  const minDimension = Math.min(window.innerWidth, window.innerHeight);

  let {
    children,
    open = $bindable(false),
    top = $bindable(50),
    left = $bindable(50),
    width = $bindable(minDimension > 800 ? 600 : 300),
    height = $bindable(minDimension > 800 ? 600 : 300),
    ...rest
  } = $props();
  let dialog = $state();
  let pointerStart = $state();
  let dragging = $state(false);

  let zindex = $state(nextZIndex());
  $effect(() => {
    if (open) {
      zindex = nextZIndex();
    }
  });

  // If the dots pattern is redeclared (when there are multiple <Dialog>
  // components, for example), and the first one is inside of a `display:
  // none` element, then they will not render. Giving each a unique ID
  // solves this problem.
  const dotsId = randomId();

  function topPointerDown(e) {
    e.target.addEventListener("pointermove", topPointerMove);
    e.target.setPointerCapture(e.pointerId);
    pointerStart = { x: e.clientX, y: e.clientY };
    dragging = true;
  }

  function topPointerUp(e) {
    e.target.removeEventListener("pointermove", topPointerMove);
    e.target.releasePointerCapture(e.pointerId);
    dragging = false;
  }

  function topPointerMove(e) {
    const dx = e.clientX - pointerStart.x;
    left += dx;
    const dy = e.clientY - pointerStart.y;
    top += dy;
    pointerStart.x = e.clientX;
    pointerStart.y = e.clientY;
  }

  function resizePointerDown(e) {
    e.target.addEventListener("pointermove", resizePointerMove);
    e.target.setPointerCapture(e.pointerId);
    pointerStart = { x: e.clientX, y: e.clientY };
  }

  function resizePointerUp(e) {
    e.target.removeEventListener("pointermove", resizePointerMove);
    e.target.releasePointerCapture(e.pointerId);
  }

  function resizePointerMove(e) {
    const dx = e.clientX - pointerStart.x;
    width += dx;
    pointerStart.x = e.clientX;
    const dy = e.clientY - pointerStart.y;
    height += dy;
    pointerStart.y = e.clientY;
  }

  // Don't bind directly to dialog open attribute since using it to toggle is
  // not recommended
  $effect(() => {
    if (open) {
      dialog?.show();
    } else {
      dialog?.close();
    }
  });
</script>

<dialog
  bind:this={dialog}
  class:small-shadow={dragging}
  style:top="max(0px, {top}px)"
  style:left="max(0px, {left}px)"
  style:--width="min(calc(100% - {left}px), {width}px)"
  style:--height="min(calc(100% - {top}px), {height}px)"
  style:z-index={zindex}
  onpointerdown={() => (zindex = nextZIndex())}
>
  <div class="top">
    <svg class="drag" onpointerdown={topPointerDown} onpointerup={topPointerUp}>
      <defs>
        <pattern
          id="dots_{dotsId}"
          x="0"
          y="0"
          width="6"
          height="9"
          viewBox="0 0 6 9"
          patternUnits="userSpaceOnUse"
        >
          <rect x="1" y="0" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
          <rect x="1" y="3" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
          <rect x="1" y="6" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
          <rect x="4" y="2" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
          <rect x="4" y="5" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
          <rect x="4" y="8" width="1" height="1" style="fill: var(--fg-color);"
          ></rect>
        </pattern>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="url(#dots_{dotsId})"
        style="stroke: var(--bg-color); stroke-width: 2px;"
      ></rect>
    </svg>
    <Button
      square
      shadow="2px"
      style="height: 1.5em"
      onclick={() => (open = false)}
    >
      &times;
    </Button>
  </div>
  <div class="main" {...rest}>
    {@render children()}
  </div>
  <button
    class="resize"
    aria-label="Drag to reize dialog"
    onpointerdown={resizePointerDown}
    onpointerup={resizePointerUp}
  ></button>
</dialog>
